(() => {
  'use strict';
  if (window.TMDLanding) { window.TMDLanding.mount(document); return; }

  const controllers = new Map();
  const productStates = new Map();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const q = (root, selector) => root.querySelector(selector);
  const qa = (root, selector) => [...root.querySelectorAll(selector)];
  const decodeMoney = (value) => {
    const node = document.createElement('div');
    node.innerHTML = value || '';
    return node.textContent;
  };

  function publish(controller) {
    if (!controller.state) return;
    const state = { ...controller.state, busy: controller.busy, owner: controller.root };
    productStates.set(state.productId, state);
    controllers.forEach((item) => item.sync(state));
  }

  class LandingSection {
    constructor(root) {
      this.root = root;
      this.events = new AbortController();
      this.request = null;
      this.state = null;
      this.busy = false;
      this.adding = false;
      this.destroyed = false;
      this.cleanups = [];
      const signal = this.events.signal;
      root.addEventListener('click', (e) => this.click(e), { signal });
      root.addEventListener('change', (e) => this.change(e), { signal });
      root.addEventListener('submit', (e) => this.submit(e), { signal });
      root.addEventListener('shopify:block:select', (e) => this.selectBlock(e), { signal });
      this.buy = q(root, '[data-tmd-buy]');
      this.sticky = q(root, '[data-tmd-sticky]');
      this.readState();
      this.enhanceForm();
      this.reveal();
      if (this.sticky) this.initSticky();
      root.classList.add('tmd-enhanced');
    }

    readState() {
      const data = q(this.root, '[data-tmd-variant]');
      if (!data) return;
      try {
        this.state = JSON.parse(data.textContent);
        ['price', 'perPair', 'compare'].forEach((key) => {
          if (this.state[key]) this.state[key] = decodeMoney(this.state[key]);
        });
      } catch (_) { this.state = null; }
    }

    enhanceForm() {
      if (!this.state) return;
      const native = q(this.root, '[data-tmd-native]');
      if (native) { native.hidden = true; q(native, 'select').disabled = true; }
      const id = q(this.root, '[data-tmd-variant-id]');
      if (id) id.disabled = false;
      this.normalizeQuantity();
    }

    normalizeQuantity() {
      const input = q(this.root, '[data-tmd-quantity]');
      if (!input) return 1;
      const min = Number(input.min) || 1;
      const step = Number(input.step) || 1;
      const max = input.max ? Number(input.max) : Infinity;
      const proposed = Number(input.value) || min;
      const highest = Number.isFinite(max) ? min + Math.floor((max - min) / step) * step : max;
      const quantity = Math.max(min, Math.min(highest, min + Math.round((proposed - min) / step) * step));
      input.value = quantity;
      const minus = q(this.root, '[data-tmd-minus]');
      const plus = q(this.root, '[data-tmd-plus]');
      if (minus) minus.disabled = quantity <= min || this.busy;
      if (plus) plus.disabled = quantity + step > max || this.busy;
      return quantity;
    }

    sync(state) {
      if (this.root.dataset.productId !== state.productId) return;
      qa(this.root, '[data-tmd-price]').forEach((node) => { node.textContent = state.price; });
      qa(this.root, '[data-tmd-per-pair]').forEach((node) => { node.textContent = state.perPair; });
      qa(this.root, '[data-tmd-compare]').forEach((node) => {
        node.textContent = state.compare || ''; node.hidden = !state.compare;
      });
      if (this.sticky) {
        const button = q(this.sticky, '[data-tmd-sticky-add]');
        const label = q(this.sticky, '[data-tmd-sticky-label]');
        button.disabled = state.busy || !state.available;
        label.textContent = state.busy ? this.sticky.dataset.busyLabel : state.available ? this.sticky.dataset.addLabel : this.sticky.dataset.soldLabel;
        q(this.sticky, '[data-tmd-sticky-variant]').textContent = state.title === 'Default Title' ? '' : state.title || '';
      }
    }

    showSlide(id, scroll = false) {
      const slides = qa(this.root, '[data-tmd-slide]');
      const selected = slides.find((slide) => slide.dataset.tmdSlide === id);
      if (!selected) return;
      slides.forEach((slide) => { slide.hidden = slide !== selected; slide.classList.toggle('is-active', slide === selected); });
      qa(this.root, '[data-tmd-thumb]').forEach((button) => {
        const active = button.dataset.tmdThumb === id;
        button.setAttribute('aria-pressed', String(active));
        if (active && scroll) button.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: motion.matches ? 'auto' : 'smooth' });
      });
      const caption = q(this.root, '[data-tmd-photo-label]');
      const counter = q(this.root, '[data-tmd-counter]');
      if (caption) caption.textContent = selected.dataset.label;
      if (counter) counter.textContent = `${String(slides.indexOf(selected) + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
    }

    click(event) {
      const target = event.target.closest('button, a');
      if (!target) return;
      if (target.hasAttribute('data-tmd-thumb')) this.showSlide(target.dataset.tmdThumb);
      if (target.hasAttribute('data-tmd-zoom')) {
        const source = q(this.root, '.tmd-gallery-slide.is-active img');
        const dialog = q(this.root, '[data-tmd-zoom-dialog]');
        if (source && dialog && dialog.showModal) {
          const clone = source.cloneNode(true);
          clone.removeAttribute('loading');
          q(dialog, '[data-tmd-zoom-image]').replaceChildren(clone);
          dialog.showModal();
        }
      }
      if (target.hasAttribute('data-tmd-open-fit')) q(this.root, '[data-tmd-fit]')?.showModal();
      if (target.hasAttribute('data-tmd-close')) target.closest('dialog')?.close();
      if (target.hasAttribute('data-tmd-plus') || target.hasAttribute('data-tmd-minus')) {
        const input = q(this.root, '[data-tmd-quantity]');
        const direction = target.hasAttribute('data-tmd-plus') ? 1 : -1;
        input.value = this.normalizeQuantity() + direction * (Number(input.step) || 1);
        this.normalizeQuantity();
      }
      if (target.hasAttribute('data-tmd-sticky-add')) {
        const state = productStates.get(this.root.dataset.productId);
        const form = state?.owner && q(state.owner, '[data-tmd-form]');
        if (form && state.available && !state.busy) form.requestSubmit();
        else {
          const buy = q(document, '[data-tmd-buy]');
          if (buy) { buy.scrollIntoView({ behavior: motion.matches ? 'auto' : 'smooth', block: 'start' }); q(buy, 'input, select')?.focus({ preventScroll: true }); }
        }
      }
      if (target.tagName === 'A' && target.hash && target.origin === location.origin && target.pathname === location.pathname) {
        let destination;
        try { destination = document.getElementById(decodeURIComponent(target.hash.slice(1))); } catch (_) { return; }
        if (destination) { event.preventDefault(); destination.scrollIntoView({ behavior: motion.matches ? 'auto' : 'smooth', block: 'start' }); }
      }
    }

    change(event) {
      if (event.target.hasAttribute('data-tmd-option-value')) this.changeVariant(event.target);
      if (event.target.hasAttribute('data-tmd-quantity')) this.normalizeQuantity();
    }

    setBusy(value) {
      this.busy = value;
      const button = q(this.root, '[data-tmd-add]');
      const label = q(this.root, '[data-tmd-add-label]');
      if (button) { button.disabled = value || !this.state?.available; button.setAttribute('aria-busy', String(value)); }
      if (label && this.state) label.textContent = value ? this.buy.dataset.adding : this.state.available ? this.state.label : this.state.id ? this.state.soldOut : this.state.unavailable;
      const quantity = q(this.root, '[data-tmd-quantity]');
      if (quantity) quantity.disabled = value;
      qa(this.root, '[data-tmd-option-value]').forEach((input) => { input.disabled = this.adding; });
      this.normalizeQuantity();
      publish(this);
    }

    status(text, error = false) {
      const node = q(this.root, '[data-tmd-status]');
      if (node) { node.textContent = text; node.classList.toggle('is-error', error); }
    }

    async changeVariant(input) {
      if (!this.buy || this.adding) return;
      if (this.request) this.request.abort();
      const optionIds = qa(this.root, '[data-tmd-option-value]:checked').map((node) => node.dataset.tmdOptionValue);
      const productUrl = input.dataset.productUrl;
      if (productUrl && new URL(productUrl, location.origin).pathname !== new URL(this.buy.dataset.productUrl, location.origin).pathname) {
        const sibling = new URL(productUrl, location.origin);
        sibling.searchParams.set('option_values', optionIds.join(','));
        location.assign(sibling.toString()); return;
      }
      const controller = new AbortController();
      this.request = controller;
      const oldQuantity = this.normalizeQuantity();
      const activeOption = input.dataset.tmdOptionValue;
      this.setBusy(true); this.status('');
      const url = new URL(location.href);
      url.searchParams.delete('variant');
      url.searchParams.set('section_id', this.root.dataset.sectionId);
      url.searchParams.set('option_values', optionIds.join(','));
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error('variant request failed');
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const newRoot = documentFragment.getElementById(this.root.id);
        const replacement = newRoot && q(newRoot, '[data-tmd-purchase-panel]');
        if (!replacement || !q(replacement, '[data-tmd-variant]')) throw new Error('missing product selection');
        if (controller.signal.aborted || this.destroyed) return;
        q(this.root, '[data-tmd-purchase-panel]').replaceWith(replacement);
        this.readState(); this.enhanceForm();
        const quantity = q(this.root, '[data-tmd-quantity]');
        if (quantity) quantity.value = oldQuantity;
        this.normalizeQuantity();
        qa(this.root, '[data-tmd-option-value]').find((node) => node.dataset.tmdOptionValue === activeOption)?.focus({ preventScroll: true });
        if (this.buy.dataset.updateUrl === 'true' && !window.Shopify?.designMode) {
          const nextUrl = new URL(location.href);
          nextUrl.searchParams.delete('option_values');
          if (this.state.id) nextUrl.searchParams.set('variant', this.state.id);
          else { nextUrl.searchParams.delete('variant'); nextUrl.searchParams.set('option_values', optionIds.join(',')); }
          history.replaceState(history.state, '', nextUrl);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && !this.destroyed) {
          this.status(this.buy.dataset.variantError, true);
          this.state = { ...this.state, available: false };
          const hiddenId = q(this.root, '[data-tmd-variant-id]');
          if (hiddenId) hiddenId.value = '';
        }
      } finally {
        if (this.request === controller && !this.destroyed) { this.request = null; this.setBusy(false); }
      }
    }

    async submit(event) {
      if (!event.target.matches('[data-tmd-form]')) return;
      event.preventDefault();
      if (!this.state?.available || this.busy || this.request) return;
      const quantity = this.normalizeQuantity();
      if (!event.target.reportValidity()) return;
      const form = new FormData(event.target);
      form.set('id', String(this.state.id)); form.set('quantity', String(quantity));
      qa(this.root, '[data-tmd-option-value]').forEach((input) => form.delete(input.name));
      this.adding = true; this.setBusy(true); this.status('');
      try {
        const base = window.Shopify?.routes?.root || this.buy.dataset.rootUrl || '/';
        const response = await fetch(`${base.replace(/\/?$/, '/')}cart/add.js`, { method: 'POST', body: form, headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        const result = await response.json();
        if (!response.ok || result.status >= 400) throw new Error(result.description || result.message || this.buy.dataset.error);
        if (this.destroyed) return;
        this.status(this.buy.dataset.success);
        document.dispatchEvent(new CustomEvent('tmd:cart-added', { detail: result }));
        if (this.buy.dataset.afterAdd === 'checkout') location.assign(`${base.replace(/\/?$/, '/')}checkout`);
        else if (this.buy.dataset.afterAdd === 'cart') location.assign(this.buy.dataset.cartUrl || `${base.replace(/\/?$/, '/')}cart`);
      } catch (error) {
        if (!this.destroyed) {
          this.status(error.message && error.message !== 'Failed to fetch' ? error.message : this.buy.dataset.error, true);
          this.root.scrollIntoView({ behavior: motion.matches ? 'auto' : 'smooth', block: 'start' });
        }
      } finally { this.adding = false; if (!this.destroyed) this.setBusy(false); }
    }

    reveal() {
      if (this.root.dataset.reveal !== 'true' || this.root.dataset.editor === 'true' || motion.matches || !('IntersectionObserver' in window)) return;
      const nodes = qa(this.root, '[data-tmd-reveal]');
      let observer;
      try {
        observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); observer.unobserve(entry.target); }
          });
        }, { threshold: 0.04, rootMargin: '0px 0px -12px 0px' });
        const stagger = Number(this.root.dataset.revealStagger) || 0;
        nodes.forEach((node, index) => {
          if (node.getBoundingClientRect().top < innerHeight) return;
          node.style.setProperty('--tmd-delay', `${Math.min(index % 3, 2) * stagger}ms`);
          observer.observe(node); node.classList.add('tmd-reveal-pending');
        });
        this.cleanups.push(() => observer.disconnect());
      } catch (_) { nodes.forEach((node) => node.classList.remove('tmd-reveal-pending')); observer?.disconnect(); }
    }

    initSticky() {
      if (this.sticky.dataset.stickyMode === 'off') return;
      const reserve = q(this.root, '.tmd-sticky-reserve');
      const update = () => {
        const state = productStates.get(this.root.dataset.productId);
        const hero = state?.owner && q(state.owner, '[data-tmd-buy]');
        const allowed = this.sticky.dataset.stickyMode !== 'mobile' || innerWidth <= 700;
        const visible = !!hero && allowed && (hero.getBoundingClientRect().bottom < 0 || this.root.dataset.editor === 'true');
        this.sticky.classList.toggle('is-visible', visible);
        reserve.classList.toggle('is-active', allowed && !!hero);
      };
      let frame = 0;
      const schedule = () => { if (!frame) frame = requestAnimationFrame(() => { frame = 0; update(); }); };
      window.addEventListener('scroll', schedule, { passive: true, signal: this.events.signal });
      window.addEventListener('resize', schedule, { passive: true, signal: this.events.signal });
      this.cleanups.push(() => cancelAnimationFrame(frame));
      this.stickyUpdate = update;
    }

    selectBlock(event) {
      qa(this.root, '.tmd-reveal-pending').forEach((node) => node.classList.add('is-revealed'));
      const block = event.target.closest('[data-tmd-slide]');
      if (block) this.showSlide(block.dataset.tmdSlide, true);
      const details = event.target.closest('details');
      if (details) details.open = true;
    }

    destroy() {
      this.destroyed = true; this.events.abort(); this.request?.abort();
      this.cleanups.forEach((cleanup) => cleanup());
      qa(this.root, 'dialog[open]').forEach((dialog) => dialog.close());
      const saved = productStates.get(this.root.dataset.productId);
      if (saved?.owner === this.root) productStates.delete(this.root.dataset.productId);
      controllers.delete(this.root);
    }
  }

  function mount(container) {
    const roots = container.matches?.('[data-tmd-section]') ? [container] : qa(container, '[data-tmd-section]');
    roots.forEach((root) => {
      if (!controllers.has(root)) {
        try { controllers.set(root, new LandingSection(root)); }
        catch (_) { qa(root, '.tmd-reveal-pending').forEach((node) => node.classList.remove('tmd-reveal-pending')); }
      }
    });
    controllers.forEach((controller) => { if (controller.state) publish(controller); });
    controllers.forEach((controller) => {
      const state = productStates.get(controller.root.dataset.productId);
      if (state) controller.sync(state);
      controller.stickyUpdate?.();
    });
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    controllers.forEach((controller) => { if (event.target.contains(controller.root)) controller.destroy(); });
    controllers.forEach((controller) => controller.stickyUpdate?.());
  });
  motion.addEventListener?.('change', () => {
    if (motion.matches) qa(document, '.tmd-section .tmd-reveal-pending').forEach((node) => node.classList.add('is-revealed'));
  });
  window.TMDLanding = { mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
})();
